import { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import ArrowCircleRight from '@mui/icons-material/ArrowCircleRight';
import LoadingSpinner from '../../../components/LoadingSpinner';
import DatasetLinkedIcon from '@mui/icons-material/DatasetLinked';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import Row from './_Row';
import PDFDataModal from '../Modals/PDFDataModal';
import DocumentExampleModal from '../Modals/DocumentExampleModal';

const PDFUploader = ({ id, name, rowProps, handleFileChange, pdfFileName, isLoading, extractedData, disabled }) => {
  const fileInputRef = useRef(null);

  const [showExtractedData, setShowExtractedData] = useState(false);
  const [showExampleDocument, setShowExampleDocument] = useState(false);
  const [fileBuffer, setFileBuffer] = useState(null);

  const handleArrowClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset file input
      fileInputRef.current.click();
    }
  };

  const handleSelectFile = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFileBuffer(URL.createObjectURL(e.target.files[0]));
    }
    handleFileChange(e);
  };

  return (
    <>
      <PDFDataModal
        file={fileBuffer}
        data={extractedData}
        isOpen={showExtractedData}
        setIsOpen={setShowExtractedData}
      />

      <DocumentExampleModal isOpen={showExampleDocument} setIsOpen={setShowExampleDocument} />

      <Row
        name={name}
        warning={false}
        toolTip="Upload the Export Accompanying Document (EAD)"
        onAction={() => setShowExampleDocument(true)}
        actionTooltip="Click to see an example of a valid Export Accompanying Document (EAD)"
        component={
          <>
            <Box
              sx={{
                display: isLoading ? 'flex' : 'none',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Box>
                <LoadingSpinner />
              </Box>
              <Box>Extracting Data... Please Wait...</Box>
            </Box>

            <FormControl
              variant="standard"
              rowProps={rowProps}
              fullWidth
              sx={{ display: isLoading ? 'none' : 'block' }}
            >
              <input
                type="file"
                accept="application/pdf"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={(e) => handleSelectFile(e)}
                disabled={disabled}
              />
              <TextField
                fullWidth
                placeholder="Select the PDF file to upload"
                id={id}
                value={pdfFileName}
                InputProps={{
                  endAdornment: !disabled && (
                    <InputAdornment position="end">
                      <IconButton onClick={handleArrowClick} edge="end">
                        <ArrowCircleRight sx={{ color: '#72BB53', fontSize: 30 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  readOnly: true,
                }}
                disabled={disabled}
              />
              {extractedData?.blobPathName && (
                <Box sx={{ marginTop: 1 }}>
                  <Button onClick={() => setShowExtractedData(true)} startIcon={<DatasetLinkedIcon />}>
                    View Extracted Data
                  </Button>

                  <Button href={fileBuffer} target="_blank" startIcon={<PictureAsPdfIcon />}>
                    Open PDF
                  </Button>
                </Box>
              )}
            </FormControl>
          </>
        }
      />
    </>
  );
};

PDFUploader.propTypes = {
  id: PropTypes.string,
  onClick: PropTypes.func,
  name: PropTypes.string,
  rowProps: PropTypes.object,
};

export default PDFUploader;
